import { FetchBaseQueryError } from '@reduxjs/toolkit/query';
import { apiAbortController, Channel, Response } from '@ui/src';
import { FFSettingsTypes, NotificationMessage, PaymentType } from './types';
import api from '../api/api';

export const rootComponentApi = api.injectEndpoints({
  endpoints: (build) => ({
    getRootMessageToken: build.mutation<
      { data: { accessToken: string } },
      void
    >({
      query: () => ({
        url: '/v1/tokens/messaging',
        method: 'POST',
        signal: apiAbortController.getSignalToken(),
      }),
    }),
    getRootDictionaries: build.query<any, void>({
      async queryFn(_arg, _queryApi, _extraOptions, fetchWithBQ) {
        const dictionaries = await Promise.all([
          fetchWithBQ('/v1/countries?all=true'),
          fetchWithBQ('/v1/currencies?active=true&all=true'),
          fetchWithBQ('/v1/currencies/base'),
          fetchWithBQ('/v1/settings?take=9999'),
        ]);

        return dictionaries
          ? {
              data: {
                countries: dictionaries[0].data,
                currencies: dictionaries[1].data,
                baseCurrency: dictionaries[2].data,
                settings: dictionaries[3].data,
              },
            }
          : { error: { status: 'FETCH_ERROR' } as FetchBaseQueryError };
      },
    }),
    readAllNotifications: build.mutation<
      { data: { count: number } },
      { id: string; body: { messages: string[]; channelId: string } }
    >({
      query: (data) => ({
        url: `/v1/channels/${data.id}/messages/batch-read`,
        method: 'POST',
        body: data.body,
        signal: apiAbortController.getSignalToken(),
      }),
    }),
    unreadAllNotifications: build.mutation<
      { data: { count: number } },
      { id: string; body: { messages: string[]; channelId: string } }
    >({
      query: (data) => ({
        url: `/v1/channels/${data.id}/messages/batch-unread`,
        method: 'POST',
        body: data.body,
        signal: apiAbortController.getSignalToken(),
      }),
    }),
    getNotificationChannels: build.query<Response<Channel>, string>({
      query: (id) => ({
        url: `/v1/channels?channelType=NOTIFICATION&employeeId=${id}`,
        signal: apiAbortController.getSignalToken(),
      }),
    }),
    getFFSettings: build.query<{ data: FFSettingsTypes }, void>({
      query: () => ({
        url: '/v1/settings/ff',
        signal: apiAbortController.getSignalToken(),
      }),
    }),
    getNotificationFile: build.query<Response<Channel>, string>({
      query: (id) => ({
        url: `/v1/uploads/${id}/file`,
        responseHandler: (response) => response.blob(),
        signal: apiAbortController.getSignalToken(),
      }),
    }),
    getNotificationChannelMessages: build.query<
      Response<NotificationMessage>,
      { id: string; cursor?: string; refresh?: boolean }
    >({
      query: (data) => ({
        url: `/v1/channels/${
          data.id
        }/messages?take=30&sortProp=createdAt&sortDir=desc${
          data?.cursor ? `&cursor=${data.cursor}` : ''
        }`,
        signal: apiAbortController.getSignalToken(),
      }),
    }),
    getWhitelabel: build.query<
      {
        data: {
          data: {
            value: Record<string, string>;
          };
        };
      },
      void
    >({
      query: () => ({
        url: '/v1/settings/whitelabel',
      }),
    }),
    checkDocumentStringName: build.mutation<any, { value: string }>({
      query: (data) => ({
        url: '/v1/swift/check-string',
        method: 'POST',
        body: data,
        signal: apiAbortController.getSignalToken(),
      }),
    }),
    getPaymentTypes: build.query<Response<PaymentType>, void>({
      query: () => ({
        url: '/v1/payment-categories?all=true',
        signal: apiAbortController.getSignalToken(),
      }),
    }),
    getRootNotificationChannelEntity: build.query<{ data: any }, string>({
      query: (id) => ({
        url: `/v1/channels/${id}`,
        signal: apiAbortController.getSignalToken(),
      }),
    }),
    getRootApplicationNotificationEntity: build.query<{ data: any }, string>({
      query: (id) => ({
        url: `/v1/clients-applications/${id}`,
        signal: apiAbortController.getSignalToken(),
      }),
    }),
  }),
});

export const {
  useGetRootMessageTokenMutation,
  useLazyGetNotificationChannelsQuery,
  useLazyGetNotificationChannelMessagesQuery,
  useReadAllNotificationsMutation,
  useUnreadAllNotificationsMutation,
  useLazyGetNotificationFileQuery,
  useGetWhitelabelQuery,
  useLazyGetPaymentTypesQuery,
  useLazyGetRootDictionariesQuery,
  useLazyGetRootNotificationChannelEntityQuery,
  useLazyGetRootApplicationNotificationEntityQuery,
  useCheckDocumentStringNameMutation,
  useLazyGetFFSettingsQuery,
} = rootComponentApi;

export const {
  endpoints: {
    getRootMessageToken,
    getNotificationChannels,
    getNotificationChannelMessages,
    readAllNotifications,
    unreadAllNotifications,
    getNotificationFile,
    getPaymentTypes,
    getRootDictionaries,
    getRootNotificationChannelEntity,
    getRootApplicationNotificationEntity,
    checkDocumentStringName,
    getFFSettings,
  },
} = rootComponentApi;
